import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HTTPService } from '../../../services/httpService';
import ScopeService from '../../../services/scopeService';
import { getSessionsByUser } from '../../../services/timeTrackingService';
import { toast } from 'react-toastify';
import moment from 'moment';
import SessionHistory from './SessionHistory';

const API = new HTTPService();

/**
 * StaffTimeDetail - Detail view for a technician's time tracking history
 *
 * Route: /staff/:userId/time
 *
 * Props (from router):
 * - match.params.userId - The technician's user ID
 *
 * Props (from App.js):
 * - userType - Current user type
 * - typeId - Company/installer ID
 * - user - Current logged-in user object
 */
class StaffTimeDetail extends Component {
  state = {
    // Technician data
    technician: null,
    loadingTechnician: true,

    // Time tracking enabled flag
    timeTrackingEnabled: false,

    // Sessions data
    sessions: [],
    loadingSessions: true,
    error: null,

    // Date filters
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),

    // Summary stats
    stats: {
      totalHoursWeek: 0,
      totalHoursMonth: 0,
      flaggedCount: 0,
      openSessionCount: 0
    }
  };

  async componentDidMount() {
    this.loadTimeTrackingSettings();
    await this.loadTechnician();
    await this.loadSessions();
  }

  // Load time tracking enabled setting from company data
  loadTimeTrackingSettings = () => {
    try {
      const companyData = ScopeService.getCompany() || {};
      // Time tracking mode: 'none' | 'voluntary' | 'required'
      // - none: feature disabled
      // - voluntary/required: feature enabled
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      const timeTrackingEnabled = timeTrackingMode !== 'none';
      this.setState({ timeTrackingEnabled });
    } catch (error) {
      console.error('Error loading time tracking settings:', error);
    }
  }

  // Load technician details
  loadTechnician = async () => {
    const { match } = this.props;
    const userId = match?.params?.userId;

    if (!userId) {
      this.setState({ error: 'No user ID provided', loadingTechnician: false });
      return;
    }

    try {
      const { data } = await API.get(`/users/${userId}`);
      this.setState({
        technician: Array.isArray(data) ? data[0] : data,
        loadingTechnician: false
      });
    } catch (error) {
      console.error('Error loading technician:', error);
      this.setState({
        error: 'Failed to load technician details',
        loadingTechnician: false
      });
    }
  };

  // Load session history
  loadSessions = async () => {
    const { match } = this.props;
    const { startDate, endDate } = this.state;
    const userId = match?.params?.userId;

    if (!userId) return;

    try {
      this.setState({ loadingSessions: true });

      const sessions = await getSessionsByUser(userId, {
        startDate,
        endDate,
        limit: 100
      });

      // Calculate stats
      const stats = this.calculateStats(sessions);

      this.setState({
        sessions: sessions || [],
        stats,
        loadingSessions: false,
        error: null
      });
    } catch (error) {
      console.error('Error loading sessions:', error);
      this.setState({
        error: 'Failed to load session history',
        loadingSessions: false
      });
    }
  };

  // Calculate summary statistics
  calculateStats = (sessions) => {
    const weekStart = moment().startOf('week');
    const monthStart = moment().startOf('month');

    let totalSecondsWeek = 0;
    let totalSecondsMonth = 0;
    let flaggedCount = 0;
    let openSessionCount = 0;

    sessions.forEach(session => {
      const checkIn = moment(session.check_in_at);

      // Calculate seconds from timestamps if total_seconds not provided
      let seconds = session.total_seconds;
      if ((seconds === null || seconds === undefined) && session.check_out_at) {
        const checkOut = moment(session.check_out_at);
        seconds = checkOut.diff(checkIn, 'seconds');
      }
      seconds = seconds || 0;

      // Count this week
      if (checkIn.isSameOrAfter(weekStart)) {
        totalSecondsWeek += seconds;
      }

      // Count this month
      if (checkIn.isSameOrAfter(monthStart)) {
        totalSecondsMonth += seconds;
      }

      // Count flagged
      if (session.status === 'flagged') {
        flaggedCount++;
      }

      // Count open
      if (session.status === 'open') {
        openSessionCount++;
      }
    });

    // Format hours - show minutes if less than 1 hour
    const formatHours = (seconds) => {
      const hours = seconds / 3600;
      if (hours < 1 && seconds > 0) {
        const minutes = Math.round(seconds / 60);
        return `${minutes}m`;
      }
      return hours.toFixed(1);
    };

    return {
      totalHoursWeek: formatHours(totalSecondsWeek),
      totalHoursMonth: formatHours(totalSecondsMonth),
      flaggedCount,
      openSessionCount
    };
  };

  // Handle date filter changes
  handleDateChange = (field, value) => {
    this.setState({ [field]: value }, () => {
      this.loadSessions();
    });
  };

  // Handle session update (after edit or close)
  handleSessionUpdate = () => {
    this.loadSessions();
    toast.success('Session updated successfully');
  };

  render() {
    const {
      technician,
      loadingTechnician,
      timeTrackingEnabled,
      sessions,
      loadingSessions,
      error,
      startDate,
      endDate,
      stats
    } = this.state;
    const { typeId } = this.props;

    return (
      <div className="container-fluid mt-4">
        {/* Back Link */}
        <div className="mb-3">
          <Link to="/staff" className="text-decoration-none">
            <i className="fa-sharp fa-regular fa-arrow-left mr-2"></i>
            Back to Staff List
          </Link>
        </div>

        {/* Time Tracking Disabled Notice */}
        {!timeTrackingEnabled && !loadingTechnician && (
          <div className="alert alert-info mb-4">
            <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
            <strong>Time tracking is not enabled.</strong>
            {' '}Enable time tracking in your location settings to start tracking technician check-ins and check-outs.
            <Link to="/staff/time-reports" className="ml-2">
              View Reports →
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="row mb-4">
          <div className="col">
            {loadingTechnician ? (
              <div className="d-flex align-items-center">
                <div className="spinner-border spinner-border-sm mr-2"></div>
                <span>Loading...</span>
              </div>
            ) : technician ? (
              <>
                <h4 className="mb-1">
                  <i className="fa-sharp fa-regular fa-clock mr-2 text-primary"></i>
                  {technician.user_name}'s Time History
                </h4>
                <p className="text-muted mb-0">
                  {technician.Email}
                </p>
              </>
            ) : (
              <h4>Time History</h4>
            )}
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
            {error}
            <button className="btn btn-link" onClick={this.loadSessions}>
              Retry
            </button>
          </div>
        )}

        {/* Summary Stats */}
        {!loadingTechnician && technician && (
          <div className="row mb-4">
            <div className="col-6 col-md-3 mb-3">
              <div className="card bg-light">
                <div className="card-body text-center py-3">
                  <h3 className="mb-0 text-primary">{stats.totalHoursWeek}</h3>
                  <small className="text-muted">Hours This Week</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="card bg-light">
                <div className="card-body text-center py-3">
                  <h3 className="mb-0 text-primary">{stats.totalHoursMonth}</h3>
                  <small className="text-muted">Hours This Month</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="card bg-light">
                <div className="card-body text-center py-3">
                  <h3 className={`mb-0 ${stats.flaggedCount > 0 ? 'text-warning' : 'text-success'}`}>
                    {stats.flaggedCount}
                  </h3>
                  <small className="text-muted">Flagged Sessions</small>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-3">
              <div className="card bg-light">
                <div className="card-body text-center py-3">
                  <h3 className={`mb-0 ${stats.openSessionCount > 0 ? 'text-info' : 'text-muted'}`}>
                    {stats.openSessionCount}
                  </h3>
                  <small className="text-muted">Open Sessions</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date Filter */}
        <div className="card mb-4">
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-auto">
                <label className="mb-0 mr-2">From:</label>
                <input
                  type="date"
                  className="form-control form-control-sm d-inline-block"
                  style={{ width: 'auto' }}
                  value={startDate}
                  onChange={(e) => this.handleDateChange('startDate', e.target.value)}
                />
              </div>
              <div className="col-auto">
                <label className="mb-0 mr-2">To:</label>
                <input
                  type="date"
                  className="form-control form-control-sm d-inline-block"
                  style={{ width: 'auto' }}
                  value={endDate}
                  onChange={(e) => this.handleDateChange('endDate', e.target.value)}
                />
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={this.loadSessions}
                  disabled={loadingSessions}
                >
                  <i className={`fa-sharp fa-regular fa-refresh ${loadingSessions ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
              <div className="col-auto ml-auto">
                <small className="text-muted">
                  {sessions.length} session{sessions.length !== 1 ? 's' : ''} found
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Session History */}
        <SessionHistory
          sessions={sessions}
          loading={loadingSessions}
          technicianName={technician?.user_name}
          locationId={typeId}
          actorUserId={this.props.user?.User_id}
          onSessionUpdate={this.handleSessionUpdate}
        />
      </div>
    );
  }
}

export default StaffTimeDetail;
